import { ContactPropertySourceSchema, ContactPropertyTypesSchema, type TServerContactProperty } from "@/schemas/contactProperty.schema"
import { TLoginUserData } from "@/schemas/user.schema"

export const DEFAULT_USER_DATA: TLoginUserData = {
  apiToken: "",
  authToken: "",
  cluster: {
    adminUrlS: "",
    apiUrlS: "",
    chromeExtensionWebstoreId: "",
    launcherUrlS: "",
    remoteUrlS: "",
    voiceUrlS: "",
  },
  account: {
    trialActivated: false,
    users: [],
    accountSid: "",
    sendSmsEnabled: false,
    nextActions: [],
    inTrial: false,
    enableClickToCallOnlyInsideCrm: false,
    departments: [],
    syncServiceSid: "",
    timeZone: "",
    expiredActiveDaysLeft: 1,
    dispositions: [],
    powerDialerEnabled: false,
    trialExpired: false,
    trialConsumed: false,
    trialDaysRemaining: 0,
    trialMinutesRemaining: 0,
    soloTrialMinutesRemaining: 0,
    machineDetectionEnabled: false,
    crm: {
      id: "",
      availableText: "",
      alias: "",
      name: "",
      saveNoteText: "",
    },
    showSequenceFeature: false,
    enableEnterPhoneNumber: false,
    testNumber: "",
    timeToRingEndpointsSeconds: 5,
    status: "LOGGED_OUT",
    disableReferralLink: false,
    blackListed: false,
    adminEmail: "",
    videoCallEnabled: false,
    templatesEnabled: false,
    customCallerIdEnabled: false,
    clusterId: "",
    ringoNumbers: [],
    supportPhoneNumber: "",
  },
  user: {
    accountId: -1,
    vmdropSetup: false,
    disableCheckOut: false,
    checkedIn: false,
    phoneStatus: "AVAILABLE",
    id: -1,
    name: "",
    lastName: "",
    powerDialer: false,
    companyName: "",
    email: "",
    disableRecordingWarnings: true,
    departmentIds: [],
    isOwner: false,
    isAdmin: false,
    fullName: "",
    canEavesdrop: false,
    accountSmsEnabled: false,
    accountSeats: 1,
    accountState: "",
    emailMarketingOptIn: true,
    smsOptIn: true,
    canAccessMonitor: false,
  },
  preferences: {
    endPointStrategy: "",
    callerIds: [],
    autoSms: false,
    voicemailDrops: [],
    pvmEnabled: false,
    nylasAccount: null,
    endPoints: [],
    autoEmail: false,
    webEndPointEnabled: false,
    gettingStarted: false,
    autoVoicemailDrop: false,
    deviceEndPointEnabled: false,
    autoProgress: false,
  },
  baseUrl: "",
  twilio: {
    twimlToken: "",
    syncToken: "",
    secondsTtl: 60,
    ids: {
      userStatus: "",
      userPreferences: "",
      smartView: "",
      lastRing: "",
      smsHistories: "",
      contacts: "",
      intention: "",
      roster: "",
      departmentStats: "",
      userStats: "",
      account: "",
      dialerList: "",
      cluster: "",
      liveCalls: "",
      paymentStatus: "",
      recentCalls: "",
    },
  },
  dynamoDb: {
    authorizationToken: "",
    tokenTtlInSeconds: 60,
    accountId: -1,
    url: "https://ringio.com",
  },
}

export const DEFAULT_CONTACT_NAME: TServerContactProperty = {
  key: "name",
  label: "Name",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: true,
  uniqueness: false,
  id: 400,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_EMAIL: TServerContactProperty = {
  key: "email",
  label: "Email",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: true,
  uniqueness: false,
  id: 401,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_ID: TServerContactProperty = {
  key: "remoteId",
  label: "ID",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: false,
  uniqueness: true,
  id: 402,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_REMOTE_ID: TServerContactProperty = {
  key: "remoteID",
  label: "ID",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: false,
  uniqueness: true,
  id: 403,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_PHONE: TServerContactProperty = {
  key: "phone",
  label: "Phone",
  type: ContactPropertyTypesSchema.Enum.phone,
  mandatory: false,
  updatable: false,
  uniqueness: false,
  id: 404,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_COMPANY_NAME: TServerContactProperty = {
  key: "business",
  label: "Company Name",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: true,
  uniqueness: false,
  id: 405,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_TITLE: TServerContactProperty = {
  key: "title",
  label: "Title",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: true,
  uniqueness: false,
  id: 406,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_LAST_NOTE: TServerContactProperty = {
  key: "lastNote",
  label: "Last Note",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: false,
  uniqueness: false,
  id: 407,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_LAST_DISPOSITION: TServerContactProperty = {
  key: "lastDisposition",
  label: "Last Disposition",
  type: ContactPropertyTypesSchema.Enum.string,
  mandatory: false,
  updatable: false,
  uniqueness: false,
  id: 408,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_CALL_COUNTER: TServerContactProperty = {
  key: "callCounter",
  label: "Call Counter",
  type: ContactPropertyTypesSchema.Enum.number,
  mandatory: false,
  updatable: false,
  uniqueness: false,
  id: 409,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_LAST_TIME_CALLED: TServerContactProperty = {
  key: "lastTimeCalled",
  label: "Last Time Called",
  type: ContactPropertyTypesSchema.Enum.date_iso,
  mandatory: false,
  updatable: false,
  uniqueness: false,
  id: 410,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_DO_NOT_CALL: TServerContactProperty = {
  key: "DNC",
  label: "Do Not Call",
  type: ContactPropertyTypesSchema.Enum.bool,
  mandatory: false,
  updatable: true,
  uniqueness: false,
  id: 411,
  accountId: -1,
  source: ContactPropertySourceSchema.Enum.base,
}

export const DEFAULT_CONTACT_ITEMS: Array<TServerContactProperty> = [
  DEFAULT_CONTACT_NAME,
  DEFAULT_CONTACT_EMAIL,
  // DEFAULT_CONTACT_ID,
  DEFAULT_REMOTE_ID,
  DEFAULT_CONTACT_PHONE,
  DEFAULT_CONTACT_COMPANY_NAME,
  DEFAULT_CONTACT_TITLE,
  DEFAULT_CONTACT_LAST_NOTE,
  DEFAULT_CONTACT_LAST_DISPOSITION,
  DEFAULT_CONTACT_CALL_COUNTER,
  DEFAULT_CONTACT_LAST_TIME_CALLED,
  DEFAULT_CONTACT_DO_NOT_CALL,
]
